/**
 * underwriting.ts — pure investor underwriting math.
 *
 * MOVED from:
 *   web-app/src/financing.ts       (monthlyPaymentFor — rehomed here as
 *                                    monthlyPaymentForTerms so the terms
 *                                    stay coupled to the payment math)
 *   web-app/src/investorCard.ts     (thresholds, metric levels, verdict
 *                                    labels, rent-to-price rule badge,
 *                                    derived rent/DSCR/GRM/cash-flow math)
 *   web-app/src/investorDetail.ts   (SCENARIOS table, deal-verdict
 *                                    badge mapping)
 *
 * All formulas are ported faithfully from the web-app sources; nothing
 * here is invented. The web-app versions that derived these from listing
 * rows + app state are now thin wrappers that extract inputs and call
 * these pure functions.
 */

import type { EffectiveLoanTerms, LoanTermOverrides } from './financing.js';
import { FINANCING_PRESET_DEFAULTS, resolveEffectiveLoan } from './financing.ts';
import type { InvestmentAnalysisAssumptions, InvestorFinancingType } from './types.js';

// ─── Core ratios ──────────────────────────────────────────────────────────

/**
 * Cap rate: NOI ÷ price. Returns null when price is not positive —
 * a zero would read as a measured value.
 */
export function capRate(annualNOI: number, price: number): number | null {
  if (price <= 0) return null;
  return (annualNOI / price) * 100;
}

/**
 * Gross rent multiplier: price ÷ annualized rent. The GRM the card
 * falls back to when the analyzer hasn't stamped one.
 */
export function grm(price: number, annualRent: number): number | null {
  if (annualRent <= 0) return null;
  return price / annualRent;
}

/**
 * Cash-on-cash return: annual cash flow ÷ total cash invested.
 * Returns null when there is no cash invested (division by zero).
 */
export function cashOnCash(annualCashFlow: number, totalCashInvested: number): number | null {
  if (totalCashInvested <= 0) return null;
  return (annualCashFlow / totalCashInvested) * 100;
}

/**
 * DSCR: NOI ÷ annual debt service. Returns null when there is no debt
 * (cash purchase) — the deal page then shows "cash purchase", never
 * an invented ratio.
 */
export function dscr(annualNOI: number, annualDebtService: number): number | null {
  if (annualDebtService <= 0) return null;
  return annualNOI / annualDebtService;
}

// ─── Payments ─────────────────────────────────────────────────────────────

/**
 * Standard amortizing monthly P&I for a loan principal. ratePct is an
 * annual percent (e.g. 7.5); a 0% rate degrades to principal ÷ months.
 */
export function monthlyPayment(principal: number, annualRatePct: number, termYears: number): number {
  if (principal <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  const n = Math.max(Math.round(termYears * 12), 1);
  if (r <= 0) return principal / n;
  // L·r / (1 − (1+r)^−n) via log1p/expm1 — same stable form as the
  // backend's services/annuity.py. The Math.pow form lost precision
  // below ~1e-9 % and divided by zero near 0 %.
  const denom = -Math.expm1(-n * Math.log1p(r));
  return denom > 0 ? (principal * r) / denom : principal / n;
}

/** Interest-only monthly payment — the hard-money bridge model. */
export function interestOnlyMonthlyPayment(principal: number, annualRatePct: number): number {
  if (principal <= 0) return 0;
  return principal * (annualRatePct / 100 / 12);
}

/**
 * Monthly payment for a purchase price under resolved loan terms.
 * Moved verbatim from financing.ts monthlyPaymentFor; decoupled from
 * state.js — terms arrive as a parameter (see resolveEffectiveLoan).
 * Cash (100% down) yields 0.
 */
export function monthlyPaymentForTerms(price: number, terms: EffectiveLoanTerms): number {
  const loan = price * (1 - terms.downPct / 100);
  if (loan <= 0) return 0;
  if (terms.interestOnly) return interestOnlyMonthlyPayment(loan, terms.ratePct);
  return monthlyPayment(loan, terms.ratePct, terms.termYears);
}

/**
 * Total cash invested: down payment + closing costs + rehab budget.
 * Matches the deal page's "Cash to close" semantics (down + closing,
 * plus rehab when nonzero).
 */
export function totalCashInvested(
  downPayment: number,
  closingCosts: number,
  rehabBudget = 0,
): number {
  return downPayment + closingCosts + rehabBudget;
}

// ─── Derived display fallbacks (display-only, never analyzer output) ─────
// These are the parameter-decoupled ports of the investorCard.ts helpers.
// The card still labels derived figures with an "est" tag so they never
// read as analyzer output.

/**
 * Recover estimated monthly rent from the listing's own GRM:
 * price ÷ (GRM × 12) is exactly the rent the analyzer used.
 * Null when the GRM is missing or the price is invalid.
 */
export function estimatedRentFromGrm(price: number, grossRentMultiplier: number | null | undefined): number | null {
  if (price <= 0) return null;
  const g = grossRentMultiplier ?? 0;
  if (g <= 0) return null;
  return Math.round(price / (g * 12));
}

/**
 * Display-only monthly cash flow from NOI: NOI ÷ 12 − monthly debt
 * service. Mirrors investorCard's cap_rate × price derivation.
 */
export function monthlyCashFlowFromNoi(annualNOI: number, monthlyDebtService: number): number {
  return Math.round(annualNOI / 12 - monthlyDebtService);
}

// ─── Thresholds — sourced from the investment_analyzer brief ─────────────
// Keep in sync with the server-side cutoffs so UI colors match the deal
// verdict. Moved from investorCard.ts (card thresholds) and
// investorDetail.ts colorizeDealMetrics (detail thresholds — identical
// except noted below).

/** Semantic metric level; call sites map these to their own colors. */
export type MetricLevel = 'green' | 'yellow' | 'red' | 'muted';

function levelFor(value: number | null | undefined, green: number, yellow: number): MetricLevel {
  if (value == null) return 'muted';
  if (value >= green) return 'green';
  if (value >= yellow) return 'yellow';
  return 'red';
}

/** Cap rate: ≥7% green, ≥5% yellow. */
export function capRateLevel(capPct: number | null | undefined): MetricLevel {
  return levelFor(capPct, 7, 5);
}

/** Cash-on-cash: ≥10% green, ≥6% yellow. */
export function cashOnCashLevel(cocPct: number | null | undefined): MetricLevel {
  return levelFor(cocPct, 10, 6);
}

/** DSCR: ≥1.25 green (lender requirement), ≥1.0 yellow. */
export function dscrLevel(dscr: number | null | undefined): MetricLevel {
  return levelFor(dscr, 1.25, 1.0);
}

/**
 * Investment score colour, keyed to the analyzer's verdict bands so a
 * badge, a Watchlist card and the detail page's "Good deal" pill agree:
 * ≥65 (good_deal / great_deal) green, ≥50 (average) yellow, else red.
 */
export function investmentScoreLevel(score: number | null | undefined): MetricLevel {
  return levelFor(score, 65, 50);
}

/**
 * Human verdict label for an investment score. Moved from
 * investorCard.ts verdictLabel (badge copy).
 */
export function verdictForScore(score: number | null | undefined): string {
  if (score == null) return 'Analysis pending';
  if (score >= 80) return 'Great deal';
  if (score >= 65) return 'Good deal';
  if (score >= 50) return 'Average deal';
  if (score >= 35) return 'Below average';
  return 'Avoid';
}

/**
 * Semantic badge level for a backend `deal_verdict` key. Moved from
 * investorDetail.ts verdictBadge (which returned CSS classes —
 * 'inv-verdict-green' etc.); CSS class mapping stays with the apps.
 */
export function dealVerdictLevel(verdict: string): 'green' | 'yellow' | 'red' {
  switch (verdict) {
    case 'great_deal':
    case 'good_deal':
      return 'green';
    case 'average':
      return 'yellow';
    case 'below_average':
    default:
      return 'red';
  }
}

/**
 * The same verdict keys rendered as human labels
 * ('Great deal' / 'Good deal' / 'Average' / 'Below average' / 'Avoid'),
 * moved from investorDetail.ts verdictBadge.
 */
export function dealVerdictLabel(verdict: string): string {
  switch (verdict) {
    case 'great_deal':
      return 'Great deal';
    case 'good_deal':
      return 'Good deal';
    case 'average':
      return 'Average';
    case 'below_average':
      return 'Below average';
    default:
      return 'Avoid';
  }
}

// ─── Rent-to-price rule badge ─────────────────────────────────────────────
// The 1%/2% rule is the fastest sniff test investors use for first-glance
// triage: below 0.7% is essentially always a losing cash-flow deal,
// 1%+ is rare but solid, 2%+ is the unicorn. Moved from
// investorCard.ts renderRuleBadge (HTML pill rendering stays in the apps).

/** Monthly rent ÷ price as a percent. Null when inputs are invalid. */
export function rentToPriceRatio(price: number, monthlyRent: number | null | undefined): number | null {
  if (!price || price <= 0 || !monthlyRent || monthlyRent <= 0) return null;
  return (monthlyRent / price) * 100;
}

export type RentRuleBadge = 'two-percent' | 'one-percent' | 'near-miss' | 'below';

/** Badge verdict for a rent-to-price ratio percent (see above). */
export function rentRuleBadge(ratioPct: number): RentRuleBadge {
  if (ratioPct >= 2.0) return 'two-percent';
  if (ratioPct >= 1.0) return 'one-percent';
  if (ratioPct >= 0.7) return 'near-miss';
  return 'below';
}

// ─── Financing scenario table ────────────────────────────────────────────
// Moved from investorDetail.ts SCENARIOS. One row per financing preset,
// with the SAME terms and loan structure as the backend's
// FINANCING_PRESETS (mirrored in financing.ts) — a "Hard Money" row is a
// 25%-down, 11%, 12-month interest-only bridge, a "Conventional" row is
// 20% down, never a second, drifting copy of those numbers.

export interface FinancingScenario {
  key: InvestorFinancingType;
  label: string;
  blurb: string;
  down_pct: number;
  rate: number;
  term_years: number;
  interest_only: boolean;
}

const SCENARIO_COPY: Record<InvestorFinancingType, { label: string; blurb: string }> = {
  dscr: { label: 'DSCR', blurb: 'Rental income qualifies — no W-2' },
  conventional: { label: 'Conventional', blurb: 'Personal income required' },
  hard_money: { label: 'Hard Money', blurb: '12-mo interest-only bridge' },
  cash: { label: 'Cash', blurb: 'No financing' },
};

const SCENARIO_ORDER: InvestorFinancingType[] = ['dscr', 'conventional', 'hard_money', 'cash'];

function scenarioFromTerms(key: InvestorFinancingType, terms: EffectiveLoanTerms): FinancingScenario {
  return {
    key,
    ...SCENARIO_COPY[key],
    down_pct: terms.downPct,
    rate: terms.ratePct,
    term_years: terms.termYears,
    interest_only: terms.interestOnly,
  };
}

export const FINANCING_SCENARIOS: FinancingScenario[] = SCENARIO_ORDER.map((key) =>
  scenarioFromTerms(key, resolveEffectiveLoan(key)),
);

/**
 * The scenario table for one investor: the four presets, with the row
 * for the investor's OWN financing type carrying their resolved terms
 * (preset + fine-tuned down/rate/term overrides). That row is the one
 * the detail page marks "Your basis", so it must equal the hero tiles —
 * a conventional user who fine-tuned 30% down gets a 30%-down
 * Conventional row, not the 20% preset.
 */
export function financingScenariosFor(
  type: InvestorFinancingType | string | undefined,
  overrides: LoanTermOverrides = {},
): FinancingScenario[] {
  const own = (type && type in FINANCING_PRESET_DEFAULTS ? type : 'dscr') as InvestorFinancingType;
  return SCENARIO_ORDER.map((key) =>
    scenarioFromTerms(key, key === own ? resolveEffectiveLoan(own, overrides) : resolveEffectiveLoan(key)),
  );
}

/**
 * The request body for one financing-scenario row: the investor's
 * current slider assumptions (vacancy, rehab, rent override — whatever
 * the hero metrics were just recomputed with) with ONLY the financing
 * replaced by the scenario's down payment, rate, term and loan structure.
 * Sending the structure explicitly is what keeps a hard-money user's
 * "DSCR" row amortizing and everyone else's "Hard Money" row
 * interest-only; keeping the rest of the assumption set identical is
 * what makes the four rows comparable to the hero tiles.
 */
export function scenarioAssumptions(
  base: InvestmentAnalysisAssumptions,
  scenario: Pick<FinancingScenario, 'down_pct' | 'rate' | 'term_years' | 'interest_only'>,
): InvestmentAnalysisAssumptions {
  return {
    ...base,
    down_payment_pct: scenario.down_pct,
    interest_rate: scenario.rate,
    loan_term_years: scenario.term_years,
    interest_only: scenario.interest_only,
  };
}

/** Cache key for a listing's scenario sweep — any non-financing slider
 *  change (even a rent override) yields a new key, so stale rows are
 *  never reused. Financing fields are excluded: the rows override them. */
export function scenarioCacheKey(listingId: string, base: InvestmentAnalysisAssumptions): string {
  const { down_payment_pct: _d, interest_rate: _r, loan_term_years: _t, interest_only: _io, ...rest } = base;
  return `${listingId}|${JSON.stringify(rest, Object.keys(rest).sort())}`;
}
